import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { organizationsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import CountrySelect from '../components/CountrySelect';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Building2, User as UserIcon, MapPin, FileText, CheckCircle2, AlertTriangle, ArrowLeft, Save, Clock, ExternalLink, Mail, Truck, Phone, Globe, Loader2, Trash2, RefreshCw } from 'lucide-react';
import AddressBookCard from '../components/AddressBookCard';

const CURRENCIES = ['EUR', 'DKK', 'USD', 'GBP', 'SEK', 'NOK'];

// Map missing field names to form field IDs for highlighting
const MISSING_FIELD_MAP = {
    'Account type (Business or Private)': 'account-type',
    'Company name': 'company-name',
    'Full name': 'full-name',
    'Country': 'country',
    'Billing address': 'billing-address',
    'Postal code': 'billing-postal-code',
    'City': 'billing-city',
    'Billing country': 'billing-country',
    'Currency': 'currency',
    'VAT ID': 'vat-id',
    'Billing email': 'billing-email',
    'Contact person': 'contact-person',
    'Contact email': 'contact-email',
};

export default function BillingProfilePage() {
    const { user, isAdmin, activeCompany } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isApproving, setIsApproving] = useState(false);
    
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [organization, setOrganization] = useState(null);
    const [billingStatus, setBillingStatus] = useState(null);
    
    // Form state
    const [accountType, setAccountType] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [fullName, setFullName] = useState('');
    const [contactPerson, setContactPerson] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [country, setCountry] = useState('');
    const [currency, setCurrency] = useState('');
    const [billingAddress, setBillingAddress] = useState('');
    const [billingPostalCode, setBillingPostalCode] = useState('');
    const [billingCity, setBillingCity] = useState('');
    const [billingCountry, setBillingCountry] = useState('');
    const [billingEmail, setBillingEmail] = useState('');
    const [vatId, setVatId] = useState('');

    // Company brand — Website drives auto-detection of a logo. Logo is
    // an org-level asset (NOT the user's profile picture) stored on the
    // organization document as a data URL. Both are optional and don't
    // block billing approval.
    const [website, setWebsite] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [isDetectingLogo, setIsDetectingLogo] = useState(false);
    // Remember the last website we auto-detected for, so re-blurring
    // the same value doesn't refetch. Users can force a refetch via
    // the "Refresh logo" button.
    const [lastDetectedWebsite, setLastDetectedWebsite] = useState('');
    
    // Shipping address state
    const [shippingSameAsBilling, setShippingSameAsBilling] = useState(true);
    const [shippingCompanyName, setShippingCompanyName] = useState('');
    const [shippingAttentionTo, setShippingAttentionTo] = useState('');
    const [shippingStreet, setShippingStreet] = useState('');
    const [shippingPostalCode, setShippingPostalCode] = useState('');
    const [shippingCity, setShippingCity] = useState('');
    const [shippingCountry, setShippingCountry] = useState('');
    const [shippingPhone, setShippingPhone] = useState('');
    const [shippingDeliveryEmail, setShippingDeliveryEmail] = useState('');
    
    // Use active company ID for billing profile (company-scoped, not user-scoped)
    const companyId = activeCompany?.id || user?.organization_id;
    
    // Helper to check if a field is missing
    const isFieldMissing = (fieldId) => {
        if (!billingStatus?.missing_fields?.length) return false;
        const missingKeys = Object.entries(MISSING_FIELD_MAP)
            .filter(([name]) => billingStatus.missing_fields.includes(name))
            .map(([, id]) => id);
        return missingKeys.includes(fieldId);
    };
    
    // Get CSS class for missing field highlighting
    const getMissingFieldClass = (fieldId) => {
        return isFieldMissing(fieldId) 
            ? 'ring-2 ring-amber-400 border-amber-400 bg-amber-50/50' 
            : '';
    };
    
    useEffect(() => {
        if (companyId) {
            loadOrganization();
        }
    }, [companyId]);
    
    const loadOrganization = async () => {
        if (!companyId) return;
        
        try {
            setIsLoading(true);
            const [orgRes, statusRes] = await Promise.all([
                organizationsApi.getOne(companyId),
                organizationsApi.getBillingStatus(companyId)
            ]);
            
            const org = orgRes.data;
            setOrganization(org);
            setBillingStatus(statusRes.data);
            
            // Populate form
            setAccountType(org.type || '');
            setCompanyName(org.type === 'business' ? (org.name || '') : '');
            setFullName(org.type === 'private' ? (org.name || '') : (user?.name || ''));
            setContactPerson(org.contact_person || '');
            setContactEmail(org.contact_email || '');
            setContactPhone(org.contact_phone || '');
            setCountry(org.country || '');
            setCurrency(org.currency || '');
            setBillingAddress(org.billing_address_line1 || org.billing_address?.address || '');
            setBillingPostalCode(org.billing_postal_code || org.billing_address?.postal_code || '');
            setBillingCity(org.billing_city || org.billing_address?.city || '');
            setBillingCountry(org.billing_country || org.billing_address?.country || '');
            setBillingEmail(org.billing_email || '');
            setVatId(org.vat_id || '');

            // Company brand fields
            setWebsite(org.website || '');
            setLogoUrl(org.logo_url || '');
            // Seed "already detected" with whatever the persisted website
            // is so simply visiting the page doesn't kick off a network
            // call. New/edited values will still trigger detection.
            setLastDetectedWebsite(org.website || '');
            
            // Populate shipping address
            setShippingSameAsBilling(org.shipping_same_as_billing !== false);
            if (org.shipping_address) {
                setShippingCompanyName(org.shipping_address.company_name || '');
                setShippingAttentionTo(org.shipping_address.attention_to || '');
                setShippingStreet(org.shipping_address.street || '');
                setShippingPostalCode(org.shipping_address.postal_code || '');
                setShippingCity(org.shipping_address.city || '');
                setShippingCountry(org.shipping_address.country || '');
                setShippingPhone(org.shipping_address.phone || '');
                setShippingDeliveryEmail(org.shipping_address.delivery_email || '');
            }
        } catch (error) {
            console.error('Failed to load organization:', error);
            toast.error(t('billing.load_error', 'Failed to load billing profile'));
        } finally {
            setIsLoading(false);
        }
    };
    
    // Auto-set currency when country changes
    const handleCountryChange = (countryCode, countryObj) => {
        setCountry(countryCode);
        if (countryObj?.currency && !currency) {
            setCurrency(countryObj.currency);
        }
    };

    // Attempt to auto-detect the company logo for a website URL.
    // Silent by design: if nothing is found we clear the preview but
    // never toast an error (per product spec). `force` bypasses the
    // "same website as last time" guard so the Refresh button always
    // hits the network.
    const detectLogo = async (siteValue, { force = false } = {}) => {
        const trimmed = (siteValue || '').trim();
        if (!trimmed) return;
        if (!companyId) return;
        if (!force && trimmed === lastDetectedWebsite) return;
        setIsDetectingLogo(true);
        try {
            const res = await organizationsApi.detectLogo(companyId, trimmed);
            const found = res?.data?.logo_url || '';
            setLogoUrl(found);
            setLastDetectedWebsite(trimmed);
        } catch (err) {
            // Silent — never surface a detection failure as an error.
            // Leave the current logo preview as-is.
            console.warn('Logo detection failed:', err);
            setLastDetectedWebsite(trimmed);
        } finally {
            setIsDetectingLogo(false);
        }
    };

    const handleWebsiteBlur = () => {
        // Only auto-detect for business accounts (private accounts don't
        // have a Website field). Empty value = nothing to do.
        if (accountType !== 'business') return;
        detectLogo(website);
    };

    const handleRemoveLogo = () => {
        setLogoUrl('');
        // Keep `lastDetectedWebsite` so re-blurring the same site
        // doesn't silently re-fetch and undo the removal. The user
        // can hit "Refresh logo" if they want to try again.
    };
    
    const handleSave = async () => {
        try {
            setIsSaving(true);
            
            const updateData = {
                organization_type: accountType,
                contact_person: contactPerson || null,
                contact_email: contactEmail || null,
                contact_phone: contactPhone || null,
                billing_address_line1: billingAddress,
                billing_postal_code: billingPostalCode,
                billing_city: billingCity,
                billing_country: billingCountry,
                billing_email: billingEmail,
                country: country,
                currency: currency,
                shipping_same_as_billing: shippingSameAsBilling,
            };
            
            // Add type-specific fields
            if (accountType === 'business') {
                updateData.company_name = companyName;
                updateData.vat_id = vatId || null;
                // Website + logo are business-only fields (Company
                // Information card). Both round-trip on save so the
                // logo survives a page refresh even without re-detect.
                updateData.website = website || null;
                updateData.logo_url = logoUrl || '';
            } else {
                updateData.full_name = fullName;
            }
            
            // Add shipping address if different from billing
            if (!shippingSameAsBilling) {
                updateData.shipping_address = {
                    company_name: shippingCompanyName || null,
                    attention_to: shippingAttentionTo || null,
                    street: shippingStreet,
                    postal_code: shippingPostalCode,
                    city: shippingCity,
                    country: shippingCountry,
                    phone: shippingPhone,
                    delivery_email: shippingDeliveryEmail || null,
                };
            }
            
            await organizationsApi.updateBillingProfile(companyId, updateData);
            
            toast.success(t('billing.save_success', 'Billing profile saved successfully'));
            await loadOrganization(); // Reload to get updated status
        } catch (error) {
            console.error('Failed to save billing profile:', error);
            toast.error(error.response?.data?.detail || t('billing.save_error', 'Failed to save billing profile'));
        } finally {
            setIsSaving(false);
        }
    };
    
    // Check if form is complete based on account type
    const isFormComplete = useMemo(() => {
        if (!accountType) return false;
        if (!billingAddress || !billingPostalCode || !billingCity || !billingCountry) return false;
        if (!billingEmail) return false;  // Billing email is required for all accounts
        if (!country) return false;
        if (!currency) return false;  // Currency is required for all accounts
        
        if (accountType === 'business') {
            if (!companyName || !vatId) return false;
        } else {
            if (!fullName) return false;
        }
        
        // Validate shipping address if different from billing
        if (!shippingSameAsBilling) {
            if (!shippingStreet || !shippingPostalCode || !shippingCity || !shippingCountry || !shippingPhone) {
                return false;
            }
        }
        
        return true;
    }, [accountType, companyName, fullName, country, currency, billingAddress, billingPostalCode, billingCity, billingCountry, billingEmail, vatId, shippingSameAsBilling, shippingStreet, shippingPostalCode, shippingCity, shippingCountry, shippingPhone]);
    
    if (isLoading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
        );
    }
    
    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-2xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-6">
                    <Button
                        variant="ghost"
                        onClick={() => navigate(-1)}
                        className="mb-4"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        {t('billing.back', 'Back')}
                    </Button>
                    <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {t('billing.title', 'Billing Profile')}
                    </h1>
                    <p className="text-slate-600 mt-1">
                        {t('billing.description', 'Complete your billing information to submit orders')}
                    </p>
                </div>
                
                {/* Status Card - Source of Truth for Organization Approval */}
                {billingStatus && (
                    <Card className={`mb-6 ${
                        billingStatus.billing_status === 'synced_to_economic' 
                            ? 'border-emerald-300 bg-emerald-50/50' 
                            : billingStatus.billing_status === 'pending_approval'
                                ? 'border-amber-300 bg-amber-50/50'
                                : 'border-border'
                    }`}>
                        <CardContent className="pt-4">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3">
                                    {billingStatus.billing_status === 'synced_to_economic' ? (
                                        <div className="mt-0.5 p-2 rounded-full bg-emerald-100">
                                            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                                        </div>
                                    ) : billingStatus.billing_status === 'pending_approval' ? (
                                        <div className="mt-0.5 p-2 rounded-full bg-amber-100">
                                            <Clock className="h-5 w-5 text-amber-600" />
                                        </div>
                                    ) : (
                                        <div className="mt-0.5 p-2 rounded-full bg-slate-100">
                                            <AlertTriangle className="h-5 w-5 text-slate-500" />
                                        </div>
                                    )}
                                    <div>
                                        {billingStatus.billing_status === 'synced_to_economic' ? (
                                            <>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold bg-emerald-100 text-emerald-700" data-testid="billing-status-badge">
                                                        <CheckCircle2 className="h-3.5 w-3.5" />
                                                        {t('billing.status_approved', 'Approved')}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-slate-600">
                                                    {t('billing.status_approved_desc', 'Your billing profile has been verified. You can now submit orders.')}
                                                </p>
                                            </>
                                        ) : billingStatus.billing_status === 'pending_approval' ? (
                                            <>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold bg-amber-100 text-amber-700" data-testid="billing-status-badge">
                                                        <Clock className="h-3.5 w-3.5" />
                                                        {t('billing.status_pending', 'Pending Approval')}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-slate-600">
                                                    {t('billing.status_pending_desc', 'Your billing profile is complete and waiting for admin verification. You will be able to submit orders once approved.')}
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold bg-slate-100 text-slate-600" data-testid="billing-status-badge">
                                                        {t('billing.status_incomplete', 'Incomplete')}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-slate-600">
                                                    {t('billing.status_incomplete_desc', 'Please complete your billing profile. Once submitted, an admin will review and approve it.')}
                                                </p>
                                                {billingStatus.missing_fields?.length > 0 && (
                                                    <div className="mt-2 text-xs text-slate-500">
                                                        <strong>{t('billing.missing_label', 'Missing:')}</strong> {billingStatus.missing_fields.join(', ')}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Admin Approve & Sync Button - Only for pending_approval status */}
                                {isAdmin && billingStatus.billing_status === 'pending_approval' && (
                                    <Button
                                        onClick={async () => {
                                            try {
                                                setIsApproving(true);
                                                await organizationsApi.approveAndSync(companyId);
                                                toast.success('Organization approved and synced to e-conomic');
                                                await loadOrganization();
                                            } catch (error) {
                                                toast.error(error.response?.data?.detail || 'Failed to approve organization');
                                            } finally {
                                                setIsApproving(false);
                                            }
                                        }}
                                        disabled={isApproving}
                                        className="bg-emerald-600 hover:bg-emerald-700 shrink-0"
                                        data-testid="approve-sync-btn"
                                    >
                                        {isApproving ? (
                                            <span className="flex items-center gap-2">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                Syncing...
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-2">
                                                <CheckCircle2 className="h-4 w-4" />
                                                Approve & Sync
                                            </span>
                                        )}
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                )}
                
                {/* Account Type Selection */}
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg">
                            {t('billing.account_type', 'Account Type')} *
                        </CardTitle>
                        <CardDescription>
                            {t('billing.account_type_desc', 'Select whether this is a business or personal account')}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {isFieldMissing('account-type') && (
                            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className="text-sm text-amber-700 font-medium">⚠️ {t('billing.select_account_type', 'Please select an account type to continue')}</p>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <button
                                type="button"
                                onClick={() => setAccountType('private')}
                                className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                                    accountType === 'private' 
                                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700' 
                                        : isFieldMissing('account-type')
                                            ? 'border-amber-400 hover:border-amber-500 bg-amber-50/50'
                                            : 'border-border hover:border-slate-300'
                                }`}
                                data-testid="account-type-private"
                            >
                                <UserIcon className="h-6 w-6" />
                                <span className="font-medium">{t('billing.private', 'Private')}</span>
                                <span className="text-xs text-slate-500 text-center">{t('billing.private_desc', 'Personal/Individual')}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setAccountType('business')}
                                className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                                    accountType === 'business' 
                                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700' 
                                        : isFieldMissing('account-type')
                                            ? 'border-amber-400 hover:border-amber-500 bg-amber-50/50'
                                            : 'border-border hover:border-slate-300'
                                }`}
                                data-testid="account-type-business"
                            >
                                <Building2 className="h-6 w-6" />
                                <span className="font-medium">{t('billing.business', 'Business')}</span>
                                <span className="text-xs text-slate-500 text-center">{t('billing.business_desc', 'Company/Organization')}</span>
                            </button>
                        </div>
                    </CardContent>
                </Card>
                
                {/* Company/Personal Information - Only show after type selected */}
                {accountType && (
                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                {accountType === 'business' ? (
                                    <Building2 className="h-5 w-5 text-indigo-500" />
                                ) : (
                                    <UserIcon className="h-5 w-5 text-indigo-500" />
                                )}
                                {accountType === 'business' 
                                    ? t('billing.company_info', 'Company Information')
                                    : t('billing.personal_info', 'Personal Information')
                                }
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {accountType === 'business' ? (
                                <>
                                    <div className="space-y-2">
                                        <Label htmlFor="companyName" className={isFieldMissing('company-name') ? 'text-amber-700 font-semibold' : ''}>
                                            {t('billing.company_name', 'Company Name')} *
                                            {isFieldMissing('company-name') && <span className="ml-2 text-xs text-amber-600">({t('billing.required', 'Required')})</span>}
                                        </Label>
                                        <Input
                                            id="companyName"
                                            value={companyName}
                                            onChange={(e) => setCompanyName(e.target.value)}
                                            placeholder="Acme Records Ltd"
                                            className={getMissingFieldClass('company-name')}
                                            data-testid="billing-company-name"
                                        />
                                    </div>

                                    {/* Company Website + auto-detected logo — optional. Logo is
                                        stored on the organization document (NOT the user's
                                        profile picture) and never blocks approval. */}
                                    <div className="space-y-2">
                                        <Label htmlFor="companyWebsite">
                                            {t('billing.company_website', 'Company Website')}
                                        </Label>
                                        <div className="relative">
                                            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                            <Input
                                                id="companyWebsite"
                                                type="url"
                                                value={website}
                                                onChange={(e) => setWebsite(e.target.value)}
                                                onBlur={handleWebsiteBlur}
                                                placeholder="https://acme.com"
                                                className="pl-9"
                                                data-testid="billing-company-website"
                                            />
                                        </div>
                                        <p className="text-xs text-slate-500">
                                            {t('billing.logo_hint', "We'll try to pick up your logo automatically.")}
                                        </p>

                                        {(website.trim() || logoUrl || isDetectingLogo) && (
                                            <div
                                                className="flex items-center gap-4 mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200"
                                                data-testid="company-logo-preview-row"
                                            >
                                                <div className="w-16 h-16 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                                                    {isDetectingLogo ? (
                                                        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                                                    ) : logoUrl ? (
                                                        <img
                                                            src={logoUrl}
                                                            alt="Company logo"
                                                            className="max-w-full max-h-full object-contain"
                                                            data-testid="company-logo-preview-img"
                                                            onError={() => setLogoUrl('')}
                                                        />
                                                    ) : (
                                                        <Building2 className="h-6 w-6 text-slate-300" />
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-slate-700">
                                                        {isDetectingLogo
                                                            ? t('billing.logo_fetching', 'Fetching logo…')
                                                            : logoUrl
                                                                ? t('billing.logo_detected', 'Logo detected from website')
                                                                : t('billing.logo_none', 'No logo yet')}
                                                    </p>
                                                    <p className="text-xs text-slate-500 mt-0.5">
                                                        {t('billing.logo_autodetect_desc', 'Auto-detected from your website. You can refresh or remove it below.')}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => detectLogo(website, { force: true })}
                                                        disabled={isDetectingLogo || !website.trim()}
                                                        data-testid="refresh-company-logo-btn"
                                                    >
                                                        <RefreshCw className={`h-3.5 w-3.5 mr-1 ${isDetectingLogo ? 'animate-spin' : ''}`} />
                                                        {t('billing.logo_refresh', 'Refresh')}
                                                    </Button>
                                                    {logoUrl && !isDetectingLogo && (
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={handleRemoveLogo}
                                                            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                                            data-testid="remove-company-logo-btn"
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                                                            {t('billing.logo_remove', 'Remove')}
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Contact Person */}
                                    <div className="space-y-2">
                                        <Label htmlFor="contactPerson">{t('billing.contact_person', 'Contact Person')}</Label>
                                        <Input
                                            id="contactPerson"
                                            value={contactPerson}
                                            onChange={(e) => setContactPerson(e.target.value)}
                                            placeholder="John Smith"
                                            data-testid="billing-contact-person"
                                        />
                                    </div>
                                    
                                    {/* Contact Email & Phone */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="contactEmail">{t('billing.contact_email', 'Email')}</Label>
                                            <div className="relative">
                                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                                <Input
                                                    id="contactEmail"
                                                    type="email"
                                                    value={contactEmail}
                                                    onChange={(e) => setContactEmail(e.target.value)}
                                                    placeholder="contact@company.com"
                                                    className="pl-9"
                                                    data-testid="billing-contact-email"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="contactPhone">{t('billing.contact_phone', 'Phone')}</Label>
                                            <div className="relative">
                                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                                <Input
                                                    id="contactPhone"
                                                    type="tel"
                                                    value={contactPhone}
                                                    onChange={(e) => setContactPhone(e.target.value)}
                                                    placeholder="+45 12 34 56 78"
                                                    className="pl-9"
                                                    data-testid="billing-contact-phone"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="country" className={isFieldMissing('country') ? 'text-amber-700 font-semibold' : ''}>
                                                {t('billing.country', 'Country')} *
                                                {isFieldMissing('country') && <span className="ml-2 text-xs text-amber-600">({t('billing.required', 'Required')})</span>}
                                            </Label>
                                            <CountrySelect
                                                value={country}
                                                onChange={handleCountryChange}
                                                testid="billing-country"
                                                className={getMissingFieldClass('country')}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="currency" className={isFieldMissing('currency') ? 'text-amber-700 font-semibold' : ''}>
                                                {t('billing.currency', 'Currency')} *
                                                {isFieldMissing('currency') && <span className="ml-2 text-xs text-amber-600">({t('billing.required', 'Required')})</span>}
                                            </Label>
                                            <Select value={currency} onValueChange={setCurrency}>
                                                <SelectTrigger data-testid="billing-currency" className={getMissingFieldClass('currency')}>
                                                    <SelectValue placeholder={t('billing.select_generic', 'Select...')} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {CURRENCIES.map((c) => (
                                                        <SelectItem key={c} value={c}>{c}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="space-y-2">
                                        <Label htmlFor="fullName" className={isFieldMissing('full-name') ? 'text-amber-700 font-semibold' : ''}>
                                            {t('billing.full_name', 'Full Name')} *
                                            {isFieldMissing('full-name') && <span className="ml-2 text-xs text-amber-600">({t('billing.required', 'Required')})</span>}
                                        </Label>
                                        <Input
                                            id="fullName"
                                            value={fullName}
                                            onChange={(e) => setFullName(e.target.value)}
                                            placeholder="John Doe"
                                            className={getMissingFieldClass('full-name')}
                                            data-testid="billing-full-name"
                                        />
                                    </div>
                                    
                                    {/* Contact Email & Phone for Private accounts */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="contactEmail">{t('billing.contact_email', 'Email')}</Label>
                                            <div className="relative">
                                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                                <Input
                                                    id="contactEmail"
                                                    type="email"
                                                    value={contactEmail}
                                                    onChange={(e) => setContactEmail(e.target.value)}
                                                    placeholder="john@example.com"
                                                    className="pl-9"
                                                    data-testid="billing-contact-email"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="contactPhone">{t('billing.contact_phone', 'Phone')}</Label>
                                            <div className="relative">
                                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                                <Input
                                                    id="contactPhone"
                                                    type="tel"
                                                    value={contactPhone}
                                                    onChange={(e) => setContactPhone(e.target.value)}
                                                    placeholder="+45 12 34 56 78"
                                                    className="pl-9"
                                                    data-testid="billing-contact-phone"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label htmlFor="country">{t('billing.country', 'Country')} *</Label>
                                        <CountrySelect
                                            value={country}
                                            onChange={handleCountryChange}
                                            testid="billing-country"
                                        />
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>
                )}
                
                {/* Billing Address - Only show after type selected */}
                {accountType && (
                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <MapPin className="h-5 w-5 text-indigo-500" />
                                {t('billing.address', 'Billing Address')}
                            </CardTitle>
                            <CardDescription>
                                {t('billing.address_desc', 'The address where invoices will be sent')}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="billingAddress" className={isFieldMissing('billing-address') ? 'text-amber-700 font-semibold' : ''}>
                                    {t('billing.street', 'Street Address')} *
                                    {isFieldMissing('billing-address') && <span className="ml-2 text-xs text-amber-600">({t('billing.required', 'Required')})</span>}
                                </Label>
                                <Input
                                    id="billingAddress"
                                    value={billingAddress}
                                    onChange={(e) => setBillingAddress(e.target.value)}
                                    placeholder="123 Main Street"
                                    className={getMissingFieldClass('billing-address')}
                                    data-testid="billing-address"
                                />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="billingPostalCode" className={isFieldMissing('billing-postal-code') ? 'text-amber-700 font-semibold' : ''}>
                                        {t('billing.postal_code', 'Postal Code')} *
                                        {isFieldMissing('billing-postal-code') && <span className="ml-2 text-xs text-amber-600">({t('billing.required', 'Required')})</span>}
                                    </Label>
                                    <Input
                                        id="billingPostalCode"
                                        value={billingPostalCode}
                                        onChange={(e) => setBillingPostalCode(e.target.value)}
                                        placeholder="12345"
                                        className={getMissingFieldClass('billing-postal-code')}
                                        data-testid="billing-postal-code"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="billingCity" className={isFieldMissing('billing-city') ? 'text-amber-700 font-semibold' : ''}>
                                        {t('billing.city', 'City')} *
                                        {isFieldMissing('billing-city') && <span className="ml-2 text-xs text-amber-600">({t('billing.required', 'Required')})</span>}
                                    </Label>
                                    <Input
                                        id="billingCity"
                                        value={billingCity}
                                        onChange={(e) => setBillingCity(e.target.value)}
                                        placeholder="Copenhagen"
                                        className={getMissingFieldClass('billing-city')}
                                        data-testid="billing-city"
                                    />
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="billingCountry" className={isFieldMissing('billing-country') ? 'text-amber-700 font-semibold' : ''}>
                                    {t('billing.billing_country', 'Billing Country')} *
                                    {isFieldMissing('billing-country') && <span className="ml-2 text-xs text-amber-600">({t('billing.required', 'Required')})</span>}
                                </Label>
                                <CountrySelect
                                    value={billingCountry}
                                    onChange={(code) => setBillingCountry(code)}
                                    testid="billing-address-country"
                                    className={getMissingFieldClass('billing-country')}
                                />
                            </div>
                        </CardContent>
                    </Card>
                )}
                
                {/* Invoice Email - Shown for all account types */}
                {accountType && (
                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Mail className="h-5 w-5 text-indigo-500" />
                                {t('billing.invoice_email_title', 'Invoice Email')}
                            </CardTitle>
                            <CardDescription>
                                {t('billing.invoice_email_desc', 'Email address where invoices will be sent')}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                <Label htmlFor="billingEmail" className={isFieldMissing('billing-email') ? 'text-amber-700 font-semibold' : ''}>
                                    {t('billing.billing_email', 'Billing Email')} *
                                    {isFieldMissing('billing-email') && <span className="ml-2 text-xs text-amber-600">({t('billing.required', 'Required')})</span>}
                                </Label>
                                <Input
                                    id="billingEmail"
                                    type="email"
                                    value={billingEmail}
                                    onChange={(e) => setBillingEmail(e.target.value)}
                                    placeholder="invoices@example.com"
                                    className={getMissingFieldClass('billing-email')}
                                    data-testid="billing-email"
                                />
                                <p className="text-xs text-slate-500">
                                    {t('billing.billing_email_hint', 'Invoices will be sent to this email address')}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                )}
                
                {/* VAT Information - Only for Business */}
                {accountType === 'business' && (
                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <FileText className="h-5 w-5 text-indigo-500" />
                                {t('billing.vat', 'VAT Information')}
                            </CardTitle>
                            <CardDescription>
                                {t('billing.vat_required_business', 'VAT ID is required for business accounts')}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                <Label htmlFor="vatId" className={isFieldMissing('vat-id') ? 'text-amber-700 font-semibold' : ''}>
                                    {t('billing.vat_id', 'VAT ID')} *
                                    {isFieldMissing('vat-id') && <span className="ml-2 text-xs text-amber-600">({t('billing.required', 'Required')})</span>}
                                </Label>
                                <Input
                                    id="vatId"
                                    value={vatId}
                                    onChange={(e) => setVatId(e.target.value)}
                                    placeholder="DK12345678"
                                    className={getMissingFieldClass('vat-id')}
                                    data-testid="billing-vat-id"
                                />
                                <p className="text-xs text-slate-500">
                                    {t('billing.vat_format', 'Format: Country code + number (e.g., DK12345678)')}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                )}
                
                {/* Shipping Address - Shown for all account types */}
                {accountType && (
                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Truck className="h-5 w-5 text-indigo-500" />
                                {t('billing.shipping_address_title', 'Default Shipping Address')}
                            </CardTitle>
                            <CardDescription>
                                {t('billing.shipping_address_desc', 'Where should we deliver your orders?')}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Toggle for same as billing */}
                            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                                <div>
                                    <p className="font-medium text-slate-900">
                                        {t('billing.shipping_same_as_billing', 'Shipping address is same as billing address')}
                                    </p>
                                    <p className="text-sm text-slate-500 mt-1">
                                        {shippingSameAsBilling 
                                            ? t('billing.shipping_same_note', 'Orders will be shipped to your billing address')
                                            : t('billing.shipping_diff_note', 'Specify a different shipping address below')
                                        }
                                    </p>
                                </div>
                                <Switch
                                    checked={shippingSameAsBilling}
                                    onCheckedChange={setShippingSameAsBilling}
                                    data-testid="shipping-same-as-billing-toggle"
                                />
                            </div>
                            
                            {/* Shipping address form - Only show if different from billing */}
                            {!shippingSameAsBilling && (
                                <div className="space-y-4 pt-4 border-t">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="shippingCompanyName">{t('billing.company_attn', 'Company / Attention')}</Label>
                                            <Input
                                                id="shippingCompanyName"
                                                value={shippingCompanyName}
                                                onChange={(e) => setShippingCompanyName(e.target.value)}
                                                placeholder="Acme Records Ltd"
                                                data-testid="shipping-company-name"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="shippingAttentionTo">{t('billing.attention_to', 'Attention To')}</Label>
                                            <Input
                                                id="shippingAttentionTo"
                                                value={shippingAttentionTo}
                                                onChange={(e) => setShippingAttentionTo(e.target.value)}
                                                placeholder="John Smith"
                                                data-testid="shipping-attention-to"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label htmlFor="shippingStreet">{t('billing.street', 'Street Address')} *</Label>
                                        <Input
                                            id="shippingStreet"
                                            value={shippingStreet}
                                            onChange={(e) => setShippingStreet(e.target.value)}
                                            placeholder="123 Warehouse Road"
                                            data-testid="shipping-street"
                                        />
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="shippingPostalCode">{t('billing.postal_code', 'Postal Code')} *</Label>
                                            <Input
                                                id="shippingPostalCode"
                                                value={shippingPostalCode}
                                                onChange={(e) => setShippingPostalCode(e.target.value)}
                                                placeholder="12345"
                                                data-testid="shipping-postal-code"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="shippingCity">{t('billing.city', 'City')} *</Label>
                                            <Input
                                                id="shippingCity"
                                                value={shippingCity}
                                                onChange={(e) => setShippingCity(e.target.value)}
                                                placeholder="Copenhagen"
                                                data-testid="shipping-city"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label htmlFor="shippingCountry">{t('billing.country', 'Country')} *</Label>
                                        <CountrySelect
                                            value={shippingCountry}
                                            onChange={(code) => setShippingCountry(code)}
                                            testid="shipping-country"
                                        />
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="shippingPhone" className="flex items-center gap-1">
                                                <Phone className="h-3 w-3" />
                                                {t('billing.phone', 'Phone Number')} *
                                            </Label>
                                            <Input
                                                id="shippingPhone"
                                                type="tel"
                                                value={shippingPhone}
                                                onChange={(e) => setShippingPhone(e.target.value)}
                                                placeholder="+45 12345678"
                                                data-testid="shipping-phone"
                                            />
                                            <p className="text-xs text-slate-500">
                                                {t('billing.phone_hint', 'Required for delivery coordination')}
                                            </p>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="shippingDeliveryEmail" className="flex items-center gap-1">
                                                <Mail className="h-3 w-3" />
                                                {t('billing.delivery_email', 'Delivery Email')}
                                            </Label>
                                            <Input
                                                id="shippingDeliveryEmail"
                                                type="email"
                                                value={shippingDeliveryEmail}
                                                onChange={(e) => setShippingDeliveryEmail(e.target.value)}
                                                placeholder="warehouse@example.com"
                                                data-testid="shipping-delivery-email"
                                            />
                                            <p className="text-xs text-slate-500">
                                                {t('billing.delivery_email_hint', 'For tracking notifications (optional)')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
                
                {/* Address Book — saved delivery destinations for split & standard orders */}
                {accountType && companyId && (
                    <AddressBookCard orgId={companyId} />
                )}

                {/* Save Button */}
                <div className="flex justify-end gap-3">
                    <Button
                        variant="outline"
                        onClick={() => navigate(-1)}
                    >
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={isSaving || !accountType}
                        data-testid="save-billing-profile"
                    >
                        {isSaving ? (
                            <span className="flex items-center gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                {t('common.saving', 'Saving...')}
                            </span>
                        ) : (
                            <span className="flex items-center gap-2">
                                <Save className="h-4 w-4" />
                                {t('billing.save_button', 'Save Billing Profile')}
                            </span>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
